import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { RefreshCw } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { http, apiError } from "@/lib/api";

const GRAINS = [
  { id: "signups_daily", label: "Daily (30 days)" },
  { id: "signups_monthly", label: "Monthly (12 months)" },
];

const when = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Admin() {
  const { accent } = useApp();
  const [stats, setStats] = useState(null);
  const [grain, setGrain] = useState("signups_daily");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await http.get("/admin/stats");
      setStats(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const t = stats?.totals;

  return (
    <div className="space-y-5" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-xs">App analytics · super admin</p>
          <h1 className="mt-2 font-head text-4xl font-light tracking-tight text-slate-900">
            Who's <span className="font-extrabold">using DentMemo</span>
          </h1>
        </div>
        <button data-testid="admin-refresh" onClick={load} disabled={busy} className="dm-btn-ghost">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && <p data-testid="admin-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}

      {t && (
        <>
          <div className="stagger grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-kpis">
            {[["Signups", t.signups], ["Dentists (owners)", t.owners], ["Assistants", t.assistants],
              ["Clinics", t.clinics], ["New in 7 days", t.signups_7d], ["New in 30 days", t.signups_30d],
              ["Active clinics (7d)", t.active_7d], ["Clinics with any visit", t.clinics_with_activity],
              ["Patients logged", t.patients], ["Visits logged", t.visits]].map(([label, value]) => (
              <div key={label} className="dm-panel p-5" data-testid={`admin-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="label-xs">{label}</p>
                <p className="mt-2.5 font-mono text-2xl font-semibold text-slate-800">{value}</p>
              </div>
            ))}
          </div>

          <section className="dm-panel p-5 md:p-7">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-head text-xl font-semibold text-slate-800">New signups</h2>
              <div className="flex flex-wrap gap-2" data-testid="admin-grain">
                {GRAINS.map((g) => (
                  <button key={g.id} data-testid={`admin-grain-${g.id}`} onClick={() => setGrain(g.id)}
                    className={`dm-chip ${grain === g.id ? "dm-chip-on" : ""}`}>{g.label}</button>
                ))}
              </div>
            </div>
            {(stats[grain] || []).length === 0 ? (
              <div className="dm-empty" data-testid="admin-chart-empty">No signups in this window yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats[grain]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, color: "#0f172a", boxShadow: "0 8px 24px rgba(15,23,42,.12)" }} />
                  <Bar dataKey="count" fill={accent} radius={[6, 6, 0, 0]} maxBarSize={44} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>

          <section className="dm-panel">
            <div className="dm-panel-head">
              <h2 className="font-head text-lg font-semibold text-slate-800">Clinics</h2>
              <span className="text-xs text-slate-500">Newest first · usage only, no revenue</span>
            </div>
            <div className="max-h-[65vh] overflow-auto">
              <table className="dm-table w-full" data-testid="admin-clinics-table">
                <thead>
                  <tr>
                    <th>Clinic</th><th>City</th><th>Owner</th><th>Signed up</th>
                    <th className="text-right">Staff</th><th className="text-right">Patients</th>
                    <th className="text-right">Visits</th><th>Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.clinics_table.map((c) => (
                    <tr key={c.clinic_id} data-testid={`admin-clinic-row-${c.clinic_id}`}>
                      <td className="font-semibold text-slate-800">{c.name}</td>
                      <td className="text-slate-600">{c.city}</td>
                      <td className="text-slate-600">
                        <span className="block truncate text-slate-700">{c.owner_name}</span>
                        <span className="block truncate text-xs text-slate-500">{c.owner_email}</span>
                      </td>
                      <td className="text-slate-600">{when(c.created_at)}</td>
                      <td className="text-right font-mono text-slate-700">{c.assistants}</td>
                      <td className="text-right font-mono text-slate-700">{c.patients}</td>
                      <td className="text-right font-mono text-slate-700">{c.visits}</td>
                      <td className={c.last_activity ? "text-slate-600" : "text-slate-500"}>{when(c.last_activity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

        </>
      )}
    </div>
  );
}
